import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, Modal, Toast, EmptyState } from "@/components/Layout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API, soft } from "@/lib/api";
import useFilters from "@/lib/useFilters";
import FilterActions from "@/components/FilterActions";
import MultiSelect from "@/components/MultiSelect";
import PurgeButton from "@/components/PurgeButton";
import Select from "@/components/Select";
import { lock } from "@/lib/perm";
import ViewOnly from "@/components/ViewOnly";

import RowActions from "@/components/RowActions";
const FUNCTIE_LABELS = { conducere: "De conducere", executie: "De execuție" };  // „Tipul postului"
const DURATA_LABELS = { Determinata: "Determinată", Nedeterminata: "Nedeterminată" };
// „8h” = normă întreagă; sub 8 ore = parțială
function normaLabel(norm) {
  const h = parseFloat(String(norm || "").replace(",", "."));
  if (!norm) return "—";
  if (Number.isNaN(h)) return String(norm);
  return `${h >= 8 ? "Întreagă" : "Parțială"} (${norm})`;
}
// Coloanele ecranului: cele de bază sunt afișate implicit; restul (Cod, Tipul postului, funcțiile de execuție/conducere,
// Încadrarea) se adaugă din „Coloane”. Alegerea se ține în browser. „Nr.” și butoanele rămân mereu.
const SCREEN_COLUMNS = [
  { key: "ocupant", label: "Ocupant", base: true }, { key: "status", label: "Status", base: true },
  { key: "cim", label: "Număr / dată CIM", base: true }, { key: "stare_cim", label: "Stare CIM", base: true },
  { key: "fn_contractuala", label: "Funcția contractuală", base: true }, { key: "grad", label: "Grad / Treaptă", base: true },
  { key: "gradatie", label: "Gradația", base: true }, { key: "studii", label: "Studii", base: true },
  { key: "norma", label: "Norma de muncă", base: true }, { key: "durata", label: "Durată CIM", base: true },
  { key: "cat_personal", label: "Categorie de personal", base: true }, { key: "cat_post", label: "Categorie de post", base: true },
  { key: "mentiuni", label: "Mențiuni", base: true },
  { key: "cod", label: "Cod poziție" }, { key: "tip_post", label: "Tipul postului" },
  { key: "fn_exec_interna", label: "Execuție: internă" }, { key: "fn_exec_cor", label: "Execuție: cod COR" },
  { key: "fn_exec_salarizare", label: "Execuție: Legea salarizării" }, { key: "fn_cond_interna", label: "Conducere: internă" },
  { key: "fn_cond_cor", label: "Conducere: cod COR" }, { key: "fn_cond_salarizare", label: "Conducere: Legea salarizării" },
  { key: "incadrare", label: "Funcția postului" },
];
const BASE_COLUMNS = SCREEN_COLUMNS.filter(c => c.base).map(c => c.key);
const COLUMNS_KEY = "onomad_stat_screen_columns";
const STATUS_BADGE_CLASS = {
  ocupat: "badge-active", temporar_ocupat: "badge-submitted",
  temporar_vacant: "badge-pending", vacant: "badge-inactive",
};
const ALL_COLUMNS = [
  ["nr_crt", "Nr. crt."], ["cod_pozitie", "Cod poziție"], ["marca", "Marcă"], ["nume", "Nume"], ["prenume", "Prenume"],
  ["fostul_ocupant", "Fostul ocupant al postului"], ["status", "Temporar Vacant/Temporar ocupat"],
  ["numar_cim", "Număr CIM"], ["data_cim", "Dată încheiere CIM"], ["functie_contractuala", "Tipul postului"],
  ["fn_contractuala", "Funcție contractuală"],
  ["fn_exec_interna", "Funcție de execuție internă"], ["fn_exec_cor", "Funcție de execuție cod COR"], ["fn_exec_salarizare", "Funcție de execuție Legea salarizării"],
  ["fn_cond_interna", "Funcție de conducere internă"], ["fn_cond_cor", "Funcție de conducere cod COR"], ["fn_cond_salarizare", "Funcție de conducere Legea salarizării"],
  ["incadrare", "Funcția postului"], ["grad_treapta", "Grad/Treaptă"], ["gradatie", "Gradație"],
  ["studii", "Studii"], ["norma", "Norma de muncă"], ["stare_cim", "Stare CIM"],
  ["durata_cim", "Durată CIM"], ["categorie_personal", "Categorie de personal"],
  ["categorie_post", "Categorie post"], ["cumul_pensie", "Cumul pensie"], ["mentiuni", "Mențiuni"],
];

function computeLevel(unitId, byId) {
  let level = 1, cur = byId[unitId];
  while (cur && cur.parent_id && byId[cur.parent_id]) { level++; cur = byId[cur.parent_id]; if (level > 20) break; }
  return level;
}

export default function StatFunctii() {
  const { has } = useAuth();
  const nav = useNavigate();
  const canManage = has("stat_functii.update");   // mențiunile se editează direct în tabel
  const [posts, setPosts] = useState([]);
  const [units, setUnits] = useState([]);
  const [cfg, setCfg] = useState(null); // {personnel_categories, education_levels}
  const [postCfg, setPostCfg] = useState(null); // {post_categories}
  const [statFns, setStatFns] = useState([]);   // nomenclatorul „Funcție stat de funcții” (alegerea pentru „Funcție” la post)
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [mentiuniDraft, setMentiuniDraft] = useState({}); // postId -> text in progress
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [newVersion, setNewVersion] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportCols, setExportCols] = useState(ALL_COLUMNS.map(([k]) => k));
  const [rate, setRate] = useState(null);
  const [cols, setCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(COLUMNS_KEY)); return Array.isArray(v) ? v.filter(k => SCREEN_COLUMNS.some(c => c.key === k)) : BASE_COLUMNS; } catch { return BASE_COLUMNS; }
  });
  const pickCols = (v) => { setCols(v); try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(v)); } catch { /* fără stocare */ } };
  const showX = (k) => cols.includes(k);
  // toate filtrele se pot alege în mod multiplu (liste goale = toate); lista, rezumatul și exporturile le urmează
  // cu „Aplică filtre”: implicit se afișează toate posturile; `filters` = filtrele aplicate (lista, rezumatul și exporturile)
  const sf = useFilters({ org_unit_id: [], personnel_category: [], education_level: [], post_category: [] });
  const filters = sf.applied;
  const filterParams = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, vals]) => vals.forEach(v => params.append(k, v)));
    return params.toString();
  };

  const load = async () => {
    try {
      const [p, u, c, pc, fn] = await Promise.all([
        api.get(`/stat-functii/posts?${filterParams()}`), soft(api.get("/mdm/org-units"), []), soft(api.get("/config"), null), soft(api.get("/stat-functii/config"), null),
        soft(api.get("/mdm/functions", { params: { kind: "stat" } }), []),
      ]);
      setPosts(p.data); setUnits(u.data); setCfg(c.data); setPostCfg(pc.data);
      setStatFns([...(fn.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro")));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadRate = async () => {
    const r = await api.get(`/stat-functii/occupancy-rate?${filterParams()}`);
    setRate(r.data);
  };
  const loadPosts = async () => {
    const r = await api.get(`/stat-functii/posts?${filterParams()}`);
    setPosts(r.data);
  };
  useEffect(() => { if (!loading) loadPosts(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loading) loadRate(); }, [filters, loading]);

  const unitsById = useMemo(() => Object.fromEntries(units.map(u => [u.id, u])), [units]);
  const postCategories = useMemo(() => postCfg?.post_categories || [], [postCfg]); // [{name, personnel_categories}]
  // Nicio categorie de post configurată încă = nerestricționat (toată lista globală).
  const personnelUnion = useMemo(() => {
    const union = [];
    postCategories.forEach(c => (c.personnel_categories || []).forEach(pc => { if (!union.includes(pc)) union.push(pc); }));
    return union.length ? union : (cfg?.personnel_categories || []);
  }, [postCategories, cfg]);
  const allowedPersonnelFor = (categoriePostName) => {
    const match = postCategories.find(c => c.name === categoriePostName);
    const allowed = match?.personnel_categories || [];
    return allowed.length ? allowed : (cfg?.personnel_categories || []);
  };
  // Ordinea (structuri după nivel și denumire, posturi după cod) și numerotarea vin de la server — aceleași ca în exporturi.
  const grouped = useMemo(() => {
    const groups = [];
    const byUnit = {};
    posts.forEach(p => {
      if (!byUnit[p.org_unit_id]) { byUnit[p.org_unit_id] = { unit: unitsById[p.org_unit_id], posts: [] }; groups.push(byUnit[p.org_unit_id]); }
      byUnit[p.org_unit_id].posts.push(p);
    });
    return groups;
  }, [posts, unitsById]);

  const newP = () => {
    const firstCategory = postCategories[0]?.name || "";
    setEditing({
      org_unit_id: units[0]?.id || "", code: "", functie_contractuala: "executie",
      categorie_post: firstCategory, incadrare_nivel: "", grad_treapta: "",
      categorie_personal_nominal: allowedPersonnelFor(firstCategory)[0] || "",
      studii_necesare: (cfg?.education_levels || [])[0] || "", norma_nominala: "8h",
    });
  };

  const savePost = async () => {
    if (!editing.org_unit_id) return setMsg({ text: "Selectează structura organizatorică", type: "error" });
    try {
      if (editing.id) await api.put(`/stat-functii/posts/${editing.id}`, editing);
      else await api.post("/stat-functii/posts", editing);
      setEditing(null); load(); setMsg({ text: "Salvat", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };
  const deactivatePost = async (id) => {
    await api.delete(`/stat-functii/posts/${id}`); load();
  };
  const saveMentiuni = async (postId) => {
    const text = mentiuniDraft[postId] ?? "";
    await api.put(`/stat-functii/posts/${postId}/mentiuni`, { mentiuni: text });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, mentiuni: text } : p));
    setMsg({ text: "Mențiune salvată", type: "success" }); setTimeout(() => setMsg({ text: "" }), 1200);
  };

  const loadVersions = async () => {
    const r = await api.get("/stat-functii/versions");
    setVersions(r.data); setShowVersions(true);
  };
  const createVersion = async () => {
    try {
      await api.post("/stat-functii/versions", newVersion);
      setNewVersion(null); setMsg({ text: "Versiune oficială creată", type: "success" });
      setTimeout(() => setMsg({ text: "" }), 1500);
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  const toggleCol = (k) => setExportCols(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  const exportUrl = (fmt) => `${API}/stat-functii/export.${fmt}?columns=${exportCols.join(",")}${filterParams() ? `&${filterParams()}` : ""}`;

  if (loading) return <div style={{ padding: 40 }}>Se încarcă...</div>;

  return (
    <>
      <PageHeader title="Stat de Funcții" subtitle={`${posts.length} posturi`}
        actions={<>
          <MultiSelect testId="sf-columns" minWidth={130} allLabel="Coloane" options={SCREEN_COLUMNS.map(c => ({ value: c.key, label: c.label }))}
            value={cols} onChange={pickCols} summaryOf={v => `Coloane (${v.length + 1})`} />
          {cols.join() !== BASE_COLUMNS.join() && <button className="btn btn-secondary" onClick={() => pickCols(BASE_COLUMNS)} data-testid="sf-columns-reset">Coloane implicite</button>}
          <button className="btn btn-secondary" onClick={() => setShowExport(true)} {...lock(has("stat_functii.export"))}>Export</button>
          <button className="btn btn-secondary" onClick={loadVersions}>Versiuni oficiale</button>
          <button className="btn btn-primary" onClick={newP} data-testid="add-post" {...lock(has("stat_functii.create"))}>+ Post nou</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <form style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: rate ? 12 : 0 }} data-testid="sf-filters"
          onSubmit={e => { e.preventDefault(); sf.apply(); }}>
          <div><label className="label">Structură</label>
            <MultiSelect testId="filter-org-unit" minWidth={220} options={units.map(u => ({ value: u.id, label: u.name }))}
              value={sf.draft.org_unit_id} onChange={v => sf.set({ org_unit_id: v })} /></div>
          <div><label className="label">Categorie personal</label>
            <MultiSelect testId="filter-personnel-category" options={personnelUnion} value={sf.draft.personnel_category}
              onChange={v => sf.set({ personnel_category: v })} /></div>
          <div><label className="label">Nivel studii</label>
            <MultiSelect testId="filter-education-level" options={cfg?.education_levels || []}
              value={sf.draft.education_level} onChange={v => sf.set({ education_level: v })} /></div>
          <div><label className="label">Categorie post</label>
            <MultiSelect testId="filter-post-category" options={postCategories.map(c => c.name)} value={sf.draft.post_category}
              onChange={v => sf.set({ post_category: v })} /></div>
          <FilterActions f={sf} testid="sf-filters" />
        </form>
        {rate && (
          <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
            <div>Total posturi: <b>{rate.total}</b></div>
            <div>Ocupate: <b>{rate.occupied}</b></div>
            <div>Vacante: <b>{rate.vacant}</b></div>
            <div>Grad de ocupare: <b>{rate.rate_percent}%</b></div>
          </div>
        )}
      </div>

      {grouped.map(({ unit, posts: unitPosts }) => (
        <div className="card" key={unit?.id || "unknown"} style={{ marginBottom: 16 }}>
          <div style={{ padding: "10px 16px", fontWeight: 700, background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
            {unit?.name || "— structură necunoscută —"}
          </div>
          <div style={{ overflowX: "auto" }}>
          <table className="tbl tbl-dense">
            <thead><tr>
              <th>Nr.</th>{showX("cod") && <th>Cod</th>}{showX("ocupant") && <th>Ocupant</th>}{showX("status") && <th>Status</th>}
              {showX("cim") && <th>Număr / dată CIM</th>}{showX("stare_cim") && <th>Stare CIM</th>}
              {showX("tip_post") && <th>Tipul postului</th>}{showX("fn_contractuala") && <th>Funcția contractuală</th>}
              {showX("fn_exec_interna") && <th>Execuție: internă</th>}{showX("fn_exec_cor") && <th>Execuție: cod COR</th>}{showX("fn_exec_salarizare") && <th>Execuție: Legea salarizării</th>}
              {showX("fn_cond_interna") && <th>Conducere: internă</th>}{showX("fn_cond_cor") && <th>Conducere: cod COR</th>}{showX("fn_cond_salarizare") && <th>Conducere: Legea salarizării</th>}
              {showX("incadrare") && <th>Funcția postului</th>}
              {showX("grad") && <th>Grad / Treaptă</th>}{showX("gradatie") && <th>Gradația</th>}{showX("studii") && <th>Studii</th>}{showX("norma") && <th>Norma de muncă</th>}
              {showX("durata") && <th>Durată CIM</th>}{showX("cat_personal") && <th>Categorie de personal</th>}{showX("cat_post") && <th>Categorie de post</th>}
              {showX("mentiuni") && <th>Mențiuni</th>}<th></th>
            </tr></thead>
            <tbody>{unitPosts.map(p => {
              // ocupant = titularul (ocupat sau suspendat = „temporar vacant”, CIM-ul rămâne afișat) sau înlocuitorul; un CIM încetat nu apare
              const occupant = (p.status === "ocupat" || p.status === "temporar_vacant") ? p.titular : (p.status === "temporar_ocupat" ? p.inlocuitor : null);
              const cim = occupant;
              const cimState = cim && cim.status !== "terminated" ? ({ active: "Activ", suspended: "Suspendat" }[cim.status] || "") : "";
              return (
                <tr key={p.id} data-testid={`post-row-${p.code}`}>
                  <td className="tabular-nums" style={{ color: "#64748B" }} data-testid={`post-nr-${p.code}`}>{p.nr_crt}</td>
                  {showX("cod") && <td style={{ fontWeight: 600 }}>{p.code || "—"}</td>}
                  {showX("ocupant") && <td>{occupant ? `${occupant.nume} ${occupant.prenume} (${occupant.marca})` : "—"}</td>}
                  {showX("status") && <td>
                    <span className={`badge ${STATUS_BADGE_CLASS[p.status] || "badge-inactive"}`}>{p.status_label}</span>
                    {p.status === "temporar_ocupat" && p.titular && (
                      <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
                        Îl înlocuiește pe {p.titular.nume} {p.titular.prenume}{p.titular.status_reason ? ` — ${p.titular.status_reason}` : ""}
                      </div>
                    )}
                    {p.fostul_ocupant && p.status === "vacant" && (
                      <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                        Fostul ocupant: {p.fostul_ocupant.nume} {p.fostul_ocupant.prenume}
                      </div>
                    )}
                  </td>}
                  {showX("cim") && <td data-testid={`post-cim-${p.code}`}>{cim && (cim.contract_number || cim.registration_date)
                    ? <><div style={{ fontWeight: 600 }}>{cim.contract_number || "—"}</div><div style={{ fontSize: 11, color: "#64748B", whiteSpace: "nowrap" }}>{cim.registration_date || ""}</div></> : "—"}</td>}
                  {showX("stare_cim") && <td>{cimState || "—"}</td>}
                  {showX("tip_post") && <td>{FUNCTIE_LABELS[p.functie_contractuala]}</td>}
                  {showX("fn_contractuala") && <td data-testid={`post-fn_contractuala-${p.code}`}>{p.fn_contractuala || "—"}</td>}
                  {["fn_exec_interna", "fn_exec_cor", "fn_exec_salarizare", "fn_cond_interna", "fn_cond_cor", "fn_cond_salarizare"].map(k => (
                    showX(k) && <td key={k} data-testid={`post-${k}-${p.code}`}>{p[k] || "—"}</td>
                  ))}
                  {showX("incadrare") && <td>{p.incadrare_nivel || "—"}</td>}
                  {showX("grad") && <td>{p.effective_grad_treapta || "—"}</td>}
                  {showX("gradatie") && <td>{cim?.gradatie ?? "—"}</td>}
                  {showX("studii") && <td>{p.effective_studii || "—"}</td>}
                  {showX("norma") && <td>{normaLabel(p.effective_norma)}</td>}
                  {showX("durata") && <td>{DURATA_LABELS[cim?.contract_duration] || "—"}</td>}
                  {showX("cat_personal") && <td>{p.effective_categorie_personal || "—"}</td>}
                  {showX("cat_post") && <td>{p.categorie_post || "—"}</td>}
                  {showX("mentiuni") && <td style={{ minWidth: 100 }}>
                    {canManage ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <input className="input" style={{ fontSize: 12 }}
                          value={mentiuniDraft[p.id] ?? p.mentiuni}
                          onChange={e => setMentiuniDraft({ ...mentiuniDraft, [p.id]: e.target.value })} />
                        <button className="btn btn-secondary" style={{ padding: "3px 8px", fontSize: 11 }}
                          onClick={() => saveMentiuni(p.id)}>✓</button>
                      </div>
                    ) : (p.mentiuni || "—")}
                  </td>}
                  <td>
                    <RowActions column testid={`row-menu-${p.code}`} items={[
                      { label: "Vizualizează", icon: "eye", testid: `view-post-${p.code}`, onClick: () => setEditing({ ...p, view: true }) },
                      { label: "Editează", icon: "edit", onClick: () => setEditing({ ...p }), ...lock(has("stat_functii.update")) },
                      has("job_descriptions.view") && { label: "Fișa de post", icon: "clipboard", testid: `jd-post-${p.code}`, onClick: () => nav(`/fisa-post/post/${p.id}`) },
                      { label: "Dezactivează", icon: "close", danger: true, btnDanger: true, onClick: () => deactivatePost(p.id), ...lock(has("stat_functii.deactivate")) },
                    ]}><PurgeButton entity="post" id={p.id} onDone={load} /></RowActions>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
          </div>
        </div>
      ))}
      {posts.length === 0 && <EmptyState text='Niciun post. Folosește "+ Post nou".' />}

      {editing && (
        <Modal title={editing.view ? "Post — vizualizare" : editing.id ? "Editează post" : "Post nou"} onClose={() => setEditing(null)} width={680}>
          <ViewOnly on={editing.view}>
          <div className="grid-2">
            <div><label className="label label-req">Structură organizatorică</label>
              <Select className="select" value={editing.org_unit_id} onChange={e => setEditing({ ...editing, org_unit_id: e.target.value })}>
                {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select></div>
            <div><label className="label">Cod poziție</label>
              <input className="input" value={editing.code} placeholder="Se generează automat (ex. COD-01)" data-testid="post-code"
                onChange={e => setEditing({ ...editing, code: e.target.value })} />
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>Unic pe angajator. Lăsat gol, primește codul structurii + „-" + număr de ordine (de la 01).</div></div>
            <div><label className="label">Tipul postului</label>
              <Select className="select" value={editing.functie_contractuala} onChange={e => setEditing({ ...editing, functie_contractuala: e.target.value })}>
                <option value="executie">De execuție</option>
                <option value="conducere">De conducere</option>
              </Select></div>
            <div><label className="label">Categorie post</label>
              <Select className="select" value={editing.categorie_post} onChange={e => {
                const newCategory = e.target.value;
                const allowed = allowedPersonnelFor(newCategory);
                const nominal = allowed.includes(editing.categorie_personal_nominal) ? editing.categorie_personal_nominal : (allowed[0] || "");
                setEditing({ ...editing, categorie_post: newCategory, categorie_personal_nominal: nominal });
              }}>
                <option value="">— nespecificată —</option>
                {postCategories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </Select></div>
            <div><label className="label">Funcție</label>
              <Select className="select" value={editing.incadrare_nivel || ""} onChange={e => setEditing({ ...editing, incadrare_nivel: e.target.value })} data-testid="post-functie">
                <option value="">— nespecificată —</option>
                {editing.incadrare_nivel && !statFns.some(f => f.name === editing.incadrare_nivel) && <option value={editing.incadrare_nivel}>{editing.incadrare_nivel}</option>}
                {statFns.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
              </Select></div>
            <div><label className="label">Grad/Treaptă (nominal, pt. post vacant)</label>
              <input className="input" value={editing.grad_treapta} onChange={e => setEditing({ ...editing, grad_treapta: e.target.value })} /></div>
            <div><label className="label">Categorie personal (nominal, pt. post vacant)</label>
              <Select className="select" value={editing.categorie_personal_nominal} onChange={e => setEditing({ ...editing, categorie_personal_nominal: e.target.value })}>
                {allowedPersonnelFor(editing.categorie_post).map(c => <option key={c}>{c}</option>)}
              </Select>
              {(() => {
                const match = postCategories.find(c => c.name === editing.categorie_post);
                return !match || !(match.personnel_categories || []).length ? (
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
                    {editing.categorie_post ? "Categorie fără restricție configurată — se arată toate categoriile de personal." : "Alege o categorie de post pentru a restrânge lista."}
                  </div>
                ) : null;
              })()}</div>
            <div><label className="label">Studii necesare (nominal, pt. post vacant)</label>
              <Select className="select" value={editing.studii_necesare} onChange={e => setEditing({ ...editing, studii_necesare: e.target.value })}>
                {(cfg?.education_levels || []).map(c => <option key={c}>{c}</option>)}
              </Select></div>
            <div><label className="label">Norma postului</label>
              <input className="input" value={editing.norma_nominala} onChange={e => setEditing({ ...editing, norma_nominala: e.target.value })} /></div>
          </div>
          </ViewOnly>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>{editing.view ? "Închide" : "Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={savePost} data-testid="save-post" {...lock(has(editing.id ? "stat_functii.update" : "stat_functii.create"))}>Salvează</button>}
          </div>
        </Modal>
      )}

      {showVersions && (
        <Modal title="Versiuni oficiale/aprobate" onClose={() => setShowVersions(false)} width={640}>
          <div style={{ marginBottom: 14 }}>
            <button className="btn btn-primary" {...lock(has("stat_functii.version_create"))}
              onClick={() => setNewVersion({ approval_number: "", approval_date: new Date().toISOString().slice(0, 10) })}>
              + Creează versiune oficială
            </button>
          </div>
          <table className="tbl">
            <thead><tr><th>Nr. aprobare</th><th>Dată aprobare</th><th>Creat la</th></tr></thead>
            <tbody>{versions.map(v => (
              <tr key={v.id}><td>{v.approval_number}</td><td>{v.approval_date}</td><td>{v.created_at?.slice(0, 10)}</td></tr>
            ))}</tbody>
          </table>
          {versions.length === 0 && <EmptyState text="Nicio versiune oficială creată încă." />}
        </Modal>
      )}

      {newVersion && (
        <Modal title="Versiune oficială nouă" onClose={() => setNewVersion(null)} width={420}>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
            Surprinde instantaneu vederea curentă. Odată creată, versiunea nu mai poate fi editată — rămâne istoric.
          </div>
          <label className="label label-req">Număr aprobare</label>
          <input className="input" value={newVersion.approval_number} onChange={e => setNewVersion({ ...newVersion, approval_number: e.target.value })} />
          <label className="label label-req" style={{ marginTop: 10, display: "block" }}>Dată aprobare</label>
          <input className="input" type="date" value={newVersion.approval_date} onChange={e => setNewVersion({ ...newVersion, approval_date: e.target.value })} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setNewVersion(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={createVersion}>Creează</button>
          </div>
        </Modal>
      )}

      {showExport && (
        <Modal title="Export Stat de Funcții" onClose={() => setShowExport(false)} width={520}>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>Alege coloanele de inclus:</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, maxHeight: 280, overflow: "auto" }}>
            {ALL_COLUMNS.map(([k, label]) => (
              <label key={k} style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={exportCols.includes(k)} onChange={() => toggleCol(k)} />{label}
              </label>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <a className="btn btn-secondary" href={exportUrl("xlsx")} target="_blank" rel="noreferrer">Excel</a>
            <a className="btn btn-secondary" href={exportUrl("csv")} target="_blank" rel="noreferrer">CSV</a>
            <a className="btn btn-primary" href={exportUrl("pdf")} target="_blank" rel="noreferrer">PDF</a>
          </div>
        </Modal>
      )}
    </>
  );
}
